import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  CreateGoalRequest,
  CreateNudgeRequest,
  Goal,
  StudentNotice,
} from "@/lib/api-types";

/**
 * Goals, rewards & nudges (Phase 5) -- parent-side mutations mirror
 * backend/functions/staxed_parent_handler/index.mjs's goal/nudge routes;
 * student-side queries read the same underlying rows through the student's
 * own /students/me/* routes.
 */

// ---------------------------------------------------------------------------
// Parent side
// ---------------------------------------------------------------------------

/** GET /parent/children/{student_user_id}/goals */
export function useChildGoals(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.goals(studentId ?? ""),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<Goal[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/goals`,
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** POST /parent/children/{student_user_id}/goals */
export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { studentId: string } & CreateGoalRequest) => {
      const { studentId, ...body } = vars;
      const res = await api.post<ApiEnvelope<Goal>>(
        `/parent/children/${encodeURIComponent(studentId)}/goals`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.parent.goals(vars.studentId) });
    },
  });
}

/** DELETE /parent/children/{student_user_id}/goals/{goal_id} -- cancel. */
export function useCancelGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { studentId: string; goalId: string }) => {
      const res = await api.delete<ApiEnvelope<{ ok: boolean }>>(
        `/parent/children/${encodeURIComponent(vars.studentId)}/goals/${encodeURIComponent(vars.goalId)}`,
      );
      return unwrap(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.parent.goals(vars.studentId) });
    },
  });
}

/** POST /parent/children/{student_user_id}/nudges */
export function useSendNudge() {
  return useMutation({
    mutationFn: async (vars: { studentId: string } & CreateNudgeRequest) => {
      const { studentId, ...body } = vars;
      const res = await api.post<ApiEnvelope<StudentNotice>>(
        `/parent/children/${encodeURIComponent(studentId)}/nudges`,
        body,
      );
      return unwrap(res.data);
    },
  });
}

// ---------------------------------------------------------------------------
// Student side
// ---------------------------------------------------------------------------

/** GET /students/me/goals */
export function useMyGoals() {
  return useQuery({
    queryKey: qk.student.goals(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<Goal[]>>("/students/me/goals");
      return unwrap(res.data) ?? [];
    },
    staleTime: 30_000,
  });
}

/** GET /students/me/notices */
export function useMyNotices() {
  return useQuery({
    queryKey: qk.student.notices(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<StudentNotice[]>>("/students/me/notices");
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** POST /students/me/notices/{notice_id}/read -- per-card dismiss, not a watermark. */
export function useMarkNoticeRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noticeId: string) => {
      const res = await api.post<ApiEnvelope<{ ok: boolean }>>(
        `/students/me/notices/${encodeURIComponent(noticeId)}/read`,
        {},
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.student.notices() });
    },
  });
}
